import {
  AttributeFilterValues,
  AttributeListItemType,
  FilterDefinition,
  FilterState,
  FilterStateApi
} from '../../../../../types'

export const isAttributeGroupSet = (
  filters: AttributeFilterValues,
  id: string,
  filtersList: AttributeListItemType[]
) =>
  !!filtersList.find(
    (it) =>
      it.keycode === id &&
      (it.values.length
        ? Array.isArray(it.values)
          ? it.values.every((it) => isAttributeSet(filters, id, it.keycode, it.value))
          : []
        : filters.find((jt) => jt.keycode === id))
  )

export const isAttributeSet = (
  filters: AttributeFilterValues,
  id: string,
  keycode: string,
  value?: string
) =>
  !!filters.find(
    (it) =>
      it.keycode === id &&
      Array.isArray(it.values[keycode]) &&
      it.values[keycode].includes(value ?? '')
  )

// attribute filter structure
// { key: [{ id, values: { keycode: ['value', ...] } }] }
// managing a three level heirarchy instead of just a array of triplets is ridiculous!
const updateFilter = ({
  filters,
  filterKey,
  flag,
  id,
  key,
  value
}: {
  filters: Record<string, AttributeFilterValues>
} & Required<FilterState>): FilterStateApi => {
  const filterSet = filters[filterKey].find((it) => it.keycode === id)
  const isGroupSelect = !key && !value

  if (flag) {
    if (filterSet) {
      const existingValues = filterSet.values[key] || []
      // recreate the filter for this id
      return {
        [filterKey]: [
          ...filters[filterKey].filter((it) => it.keycode !== id),
          {
            keycode: id,
            values: isGroupSelect ? {} : { ...filterSet.values, [key]: [...existingValues, value] }
          }
        ]
      }
    } else {
      // create new filter for this id
      return {
        [filterKey]: [
          ...filters[filterKey],
          {
            keycode: id,
            values: isGroupSelect ? {} : { [key]: [value] }
          }
        ]
      }
    }
  } else if (filterSet) {
    const newValues = filterSet.values?.[key]?.filter((it) => it !== value)
    const hasKeys = Object.keys(filterSet.values).length > 1 || newValues?.length > 0

    if (!hasKeys) {
      return {
        [filterKey]: filters[filterKey].filter((it) => it.keycode !== id)
      }
    } else {
      const updatedValues = { ...filterSet.values, [key]: newValues }

      if (newValues.length === 0) {
        delete updatedValues[key]
      }

      return {
        [filterKey]: [
          ...filters[filterKey].filter((it) => it.keycode !== id),
          { keycode: id, values: updatedValues }
        ]
      }
    }
  }
  return {}
}

const getFilterCount = (filters: Record<string, AttributeFilterValues>, filterKey: string) =>
  filters[filterKey].reduce(
    (acc, it) =>
      acc +
      (!Object.keys(it.values).length
        ? 1 // empty groups count as 1
        : Object.keys(it.values).reduce((keyCount, key) => keyCount + it.values[key].length, 0)),
    0
  )

// used to separate multi value types in the URL, used for ATTRIBUTE
const SEPARATOR = '\x1E' // ascii record separator character

const updateUrl = (
  filters: Record<string, AttributeFilterValues>,
  filterName: string,
  updatedUrlSearchParams: URLSearchParams
) => {
  // structure: { keycode, values: { key: [string, ...] } }
  filters[filterName].forEach(({ keycode, values }) => {
    // support for empty groups
    if (!Object.keys(values).length) {
      updatedUrlSearchParams.append(filterName, encodeURI(`${keycode}`))

      return
    }

    Object.keys(values).forEach((key) =>
      values[key].forEach((value) =>
        updatedUrlSearchParams.append(
          filterName,
          encodeURI(`${keycode}${SEPARATOR}${key}${SEPARATOR}${value}`)
        )
      )
    )
  })
}

const readUrl = (filterDef: FilterDefinition, urlParams: URLSearchParams) =>
  urlParams
    .getAll(filterDef.filterKey)
    // parse attribute triplet from url
    .map((param) => {
      const [keycode, key, value] = decodeURI(param).split(SEPARATOR)
      // ternary supports empty groups
      return { keycode, values: key && value ? { [key]: [value] } : {} }
    })
    // group by keycode and key
    .reduce<AttributeFilterValues>((acc, attr) => {
      const filter = acc.find((it) => it.keycode === attr.keycode)

      return !filter
        ? [...acc, attr] // simple case, w/o merge
        : [
            // merge with existing
            ...acc.filter((it) => it !== filter), // we are replacing this entry, so filter it out
            {
              ...attr,
              // update values with merged set
              values: Object.keys(attr.values).reduce((values, key) => {
                // merge values by key
                return values[key]
                  ? { ...values, [key]: [...values[key], ...attr.values[key]] } // merge
                  : { ...values, ...attr.values } // no merge, just add
              }, filter.values)
            }
          ]
    }, [])

export const stateApi = {
  getFilterCount,
  updateFilter,
  updateUrl,
  readUrl
}
